// src/hooks/useAllSubscriptions.ts
import { useEffect as useEffect2, useState as useState2 } from "react";

// src/hooks/useWeb3InboxClient.ts
import { Web3InboxClient } from "@web3inbox/core";
import { useEffect, useState } from "react";
var useWeb3InboxClient = () => {
  const [isReady, setIsReady] = useState(Web3InboxClient.getIsReady());
  const [client, setClient] = useState(
    Web3InboxClient.getIsReady() ? Web3InboxClient.getInstance() : null
  );
  useEffect(() => {
    const unsub = Web3InboxClient.watchIsReady(setIsReady);
    return () => {
      unsub();
    };
  }, []);
  useEffect(() => {
    if (isReady) {
      setClient(Web3InboxClient.getInstance());
    }
  }, [isReady]);
  if (!isReady || !client) {
    return {
      data: null,
      isLoading: true,
      error: null
    };
  }
  return {
    data: client,
    isLoading: false,
    error: null
  };
};

// src/utils/snapshots.ts
import { Web3InboxClient as Web3InboxClient2, valtio } from "@web3inbox/core";
var useSubscriptionState = () => {
  return valtio.useSnapshot(Web3InboxClient2.subscriptionState);
};
var useClientState = () => {
  return valtio.useSnapshot(Web3InboxClient2.clientState);
};

// src/hooks/useAllSubscriptions.ts
var useAllSubscriptions = (account) => {
  const { data: w3iClient, error } = useWeb3InboxClient();
  const { subscriptions: subscriptionsTrigger } = useSubscriptionState();
  const [subscriptions, setSubscriptions] = useState2([]);
  useEffect2(() => {
    if (w3iClient) {
      setSubscriptions(w3iClient.getSubscriptions(account));
    }
  }, [account, subscriptionsTrigger, w3iClient]);
  if (!w3iClient) {
    return {
      data: null,
      isLoading: true,
      error: null
    };
  }
  if (error) {
    return {
      data: null,
      isLoading: false,
      error: {
        client: error.client
      }
    };
  }
  return {
    data: subscriptions,
    isLoading: false,
    error: null
  };
};

// src/hooks/useNotificationTypes.ts
import { useEffect as useEffect3, useState as useState3 } from "react";
var useNotificationTypes = (account, domain) => {
  const { data: w3iClient } = useWeb3InboxClient();
  const { subscriptions: subscriptionsTrigger } = useSubscriptionState();
  const [data, setData] = useState3(
    w3iClient?.getNotificationTypes(account) ?? {}
  );
  const update = async (scope) => {
    return new Promise(
      async (resolve, reject) => {
        if (!w3iClient) {
          const err = new Error(
            "Web3InboxClient is not ready, cannot subscribe"
          );
          return reject(err);
        }
        return await w3iClient.update(scope, account, domain).then((res) => {
          resolve(res);
        }).catch((e) => {
          reject(e);
        });
      }
    );
  };
  useEffect3(() => {
    if (w3iClient) {
      setData(w3iClient.getNotificationTypes(account, domain));
    }
  }, [w3iClient, account, subscriptionsTrigger, domain]);
  return {
    data,
    error: null,
    isLoading: false,
    update
  };
};

// src/hooks/useNotifications.ts
import { useEffect as useEffect4, useState as useState4 } from "react";
var waitFor = async (condition) => {
  return new Promise((resolve) => {
    setInterval(() => {
      if (condition()) {
        resolve();
      }
    }, 100);
  });
};
var useNotifications = (notificationsPerPage, isInfiniteScroll, account, domain, unreadFirst = true) => {
  const { data: w3iClient } = useWeb3InboxClient();
  const [nextPage, setNextPage] = useState4();
  const [dataWrapper, setDataWrapper] = useState4({ data: [] });
  const [isLoadingNextPage, setIsLoadingNextPage] = useState4(false);
  const [errorNextPage, setErrorNextPage] = useState4(null);
  const [hasMore, setHasMore] = useState4(false);
  const [hasMoreUnread, setHasMoreUnread] = useState4(false);
  const [error, setError] = useState4(null);
  useEffect4(() => {
    if (!w3iClient)
      return;
    try {
      setIsLoadingNextPage(true);
      const { nextPage: nextPageFunc, stopWatchingNotifications } = w3iClient.pageNotifications(
        notificationsPerPage,
        isInfiniteScroll,
        account,
        domain,
        unreadFirst
      )((newData) => {
        setDataWrapper({ data: newData.notifications });
        setIsLoadingNextPage(false);
        setHasMore(newData.hasMore);
        setHasMoreUnread(newData.hasMoreUnread);
      });
      setNextPage(() => nextPageFunc);
      return () => {
        stopWatchingNotifications();
      };
    } catch (e) {
      setError(e.message);
    }
  }, [
    account,
    domain,
    notificationsPerPage,
    isInfiniteScroll,
    w3iClient,
    setDataWrapper
  ]);
  const fetchNextPage = async () => {
    setErrorNextPage(null);
    setIsLoadingNextPage(true);
    return new Promise(async (resolve) => {
      if (!nextPage) {
        await waitFor(() => Boolean(nextPage));
      }
      const nextPageFunc = nextPage;
      return await nextPageFunc().then((res) => {
        resolve(res);
        setIsLoadingNextPage(false);
      }).catch((e) => {
        setErrorNextPage(e?.message ?? "Failed to fetch next page");
        setIsLoadingNextPage(false);
      });
    });
  };
  const markNotificationsAsRead = async (notificationIds) => {
    await waitFor(() => Boolean(w3iClient));
    const w3iClientTruthy = w3iClient;
    w3iClientTruthy.markNotificationsAsRead(notificationIds, account, domain);
    setDataWrapper(({ data: notifications }) => ({
      data: notifications.map((notification) => {
        if (notificationIds.includes(notification.id)) {
          return {
            ...notification,
            isRead: true
          };
        } else {
          return notification;
        }
      })
    }));
  };
  const markAllNotificationsAsRead = async () => {
    await waitFor(() => Boolean(w3iClient));
    const w3iClientTruthy = w3iClient;
    w3iClientTruthy.markAllNotificationsAsRead(account, domain).catch(setError).then(() => {
      setDataWrapper(({ data: notifications }) => ({
        data: notifications.map((notification) => ({
          ...notification,
          isRead: true
        }))
      }));
    });
  };
  useEffect4(() => {
    setDataWrapper({ data: [] });
    setHasMore(false);
  }, [domain, account]);
  if (isLoadingNextPage) {
    return {
      data: dataWrapper.data,
      hasMore,
      hasMoreUnread,
      error: null,
      isLoading: false,
      isLoadingNextPage,
      fetchNextPage,
      markNotificationsAsRead,
      markAllNotificationsAsRead
    };
  }
  if (errorNextPage || error) {
    return {
      data: dataWrapper.data,
      hasMore,
      hasMoreUnread,
      error: errorNextPage ?? error,
      isLoading: false,
      isLoadingNextPage: false,
      fetchNextPage,
      markNotificationsAsRead,
      markAllNotificationsAsRead
    };
  }
  return {
    data: dataWrapper.data,
    hasMore,
    hasMoreUnread,
    error: null,
    isLoading: false,
    isLoadingNextPage: false,
    fetchNextPage,
    markNotificationsAsRead,
    markAllNotificationsAsRead
  };
};

// src/hooks/usePrepareRegistration.ts
import { useState as useState5 } from "react";
var usePrepareRegistration = () => {
  const { account } = useClientState();
  const { data: w3iClient } = useWeb3InboxClient();
  const [data, setData] = useState5(null);
  const [error, setError] = useState5(null);
  const [isLoading, setIsLoading] = useState5(false);
  const prepareRegistration = async () => {
    setError(null);
    setIsLoading(true);
    return new Promise(async (resolve, reject) => {
      if (!account) {
        const err = new Error("Account not set, cannot prepare registration");
        setError(err.message);
        return reject(err);
      }
      if (!w3iClient) {
        const err = new Error(
          "Web3InboxClient is not ready, cannot prepare registration"
        );
        setError(err.message);
        return reject(err);
      }
      return await w3iClient.prepareRegistration({ account }).then((res) => {
        setData(res);
        resolve(res);
      }).catch((e) => {
        setData(null);
        setError(e?.message ?? "Failed to subscribe");
        reject(e);
      }).finally(() => {
        setIsLoading(false);
      });
    });
  };
  if (isLoading) {
    return {
      data: null,
      isLoading,
      error: null,
      prepareRegistration
    };
  }
  return {
    data,
    error,
    isLoading: false,
    prepareRegistration
  };
};

// src/hooks/usePrepareRegistrationWithRecaps.ts
import { useState as useState6 } from "react";
var usePrepareRegistrationWithRecaps = () => {
  const { account } = useClientState();
  const { data: w3iClient } = useWeb3InboxClient();
  const [data, setData] = useState6(null);
  const [error, setError] = useState6(null);
  const [isLoading, setIsLoading] = useState6(false);
  const prepareRegistrationWithRecaps = async (params) => {
    setError(null);
    return new Promise(async (resolve, reject) => {
      if (!account) {
        const err = new Error("Account not set, cannot prepare registration");
        setError(err.message);
        return reject(err);
      }
      if (!w3iClient) {
        const err = new Error(
          "Web3InboxClient is not ready, cannot prepare registration"
        );
        setError(err.message);
        return reject(err);
      }
      setIsLoading(true);
      return await w3iClient.prepareRegistrationWithRecaps(params).then((res) => {
        setData(res);
        resolve(res);
      }).catch((e) => {
        setData(null);
        setError(e?.message ?? "Failed to subscribe");
        reject(e);
      }).finally(() => {
        setIsLoading(false);
      });
    });
  };
  if (isLoading) {
    return {
      data: null,
      isLoading,
      error: null,
      prepareRegistrationWithRecaps
    };
  }
  return {
    data,
    error,
    isLoading: false,
    prepareRegistrationWithRecaps
  };
};

// src/hooks/useRegister.ts
import { useState as useState7 } from "react";

// src/utils/account.ts
var getAccountFromDidPkh = (didPkh) => {
  return didPkh.split(":").slice(-3).join(":");
};

// src/hooks/useRegister.ts
var useRegister = () => {
  const { account } = useClientState();
  const { data: w3iClient } = useWeb3InboxClient();
  const [data, setData] = useState7(null);
  const [error, setError] = useState7(null);
  const [isLoading, setIsLoading] = useState7(false);
  const register = async (params) => {
    setError(null);
    setIsLoading(true);
    return new Promise(async (resolve, reject) => {
      if (!account) {
        const err = new Error("Account not set, cannot prepare registration");
        setError(err.message);
        return reject(err);
      }
      if (!w3iClient) {
        const err = new Error(
          "Web3InboxClient is not ready, cannot prepare registration"
        );
        setError(err.message);
        return reject(err);
      }
      return await w3iClient.register(params).then((res) => {
        setData(res);
        resolve(res);
      }).catch((e) => {
        if (e?.message.includes("stale identity")) {
          const account2 = getAccountFromDidPkh(
            params.registerParams.cacaoPayload.iss
          );
          return w3iClient.unregister({ account: account2 }).then(() => {
            w3iClient.register(params).then(resolve);
          });
        } else {
          setData(null);
          setError(e?.message ?? "Failed to subscribe");
          reject(e);
        }
      }).finally(() => {
        setIsLoading(false);
      });
    });
  };
  if (isLoading) {
    return {
      data: null,
      isLoading,
      error: null,
      register
    };
  }
  return {
    data,
    error,
    isLoading: false,
    register
  };
};

// src/hooks/useSubscribe.ts
import { useState as useState8 } from "react";
var useSubscribe = (account, domain) => {
  const { data: w3iClient, isLoading: isLoadingClient } = useWeb3InboxClient();
  const [data, setData] = useState8(false);
  const [error, setError] = useState8(null);
  const [isLoading, setIsLoading] = useState8(false);
  const subscribe = async (accountOverride, domainOverride) => {
    setError(null);
    setIsLoading(true);
    return new Promise(async (resolve, reject) => {
      if (!w3iClient) {
        const err = new Error("Web3InboxClient is not ready, cannot subscribe");
        setError(err.message);
        return reject(err);
      }
      return await w3iClient.subscribeToDapp(accountOverride ?? account, domainOverride ?? domain).then((res) => {
        setData(true);
        resolve(res);
      }).catch((e) => {
        setData(false);
        setError(e?.message ?? "Failed to subscribe");
        reject(e);
      }).finally(() => {
        setIsLoading(false);
      });
    });
  };
  if (isLoading) {
    return {
      data: null,
      error: null,
      isLoading,
      subscribe
    };
  }
  return {
    data,
    error,
    isLoading,
    subscribe
  };
};

// src/hooks/useSubscription.ts
import { useEffect as useEffect5, useState as useState9 } from "react";
var useSubscription = (account, domain) => {
  const { data: w3iClient, isLoading: isLoadingClient } = useWeb3InboxClient();
  const [subscription, setSubscription] = useState9(
    w3iClient?.getSubscription(account, domain) ?? null
  );
  const [watching, setWatching] = useState9(false);
  useEffect5(() => {
    if (w3iClient && !isLoadingClient) {
      setSubscription(w3iClient.getSubscription(account, domain));
    }
  }, [w3iClient, domain, account, isLoadingClient]);
  useEffect5(() => {
    if (!w3iClient || watching)
      return;
    const stopWatching = w3iClient.watchSubscription(
      (sub) => {
        setSubscription(sub);
      },
      account,
      domain
    );
    setWatching(true);
    setSubscription(w3iClient.getSubscription(account, domain));
    return () => {
      setWatching(false);
      stopWatching();
    };
  }, [account, domain, w3iClient]);
  if (isLoadingClient) {
    return {
      data: null,
      error: null,
      isLoading: true,
      watching: false,
      getSubscription: () => null
    };
  }
  return {
    data: subscription,
    error: null,
    isLoading: false,
    watching,
    getSubscription: (account2, domain2) => w3iClient.getSubscription(account2, domain2)
  };
};

// src/hooks/useUnregister.ts
import { useState as useState10 } from "react";
var useUnregister = () => {
  const { account } = useClientState();
  const { data: w3iClient } = useWeb3InboxClient();
  const [data, setData] = useState10(false);
  const [error, setError] = useState10(null);
  const [isLoading, setIsLoading] = useState10(false);
  const unregister = async () => {
    setError(null);
    setIsLoading(true);
    return new Promise(async (resolve, reject) => {
      if (!account) {
        const err = new Error("Account not set, cannot prepare registration");
        setError(err.message);
        return reject(err);
      }
      if (!w3iClient) {
        const err = new Error(
          "Web3InboxClient is not ready, cannot prepare registration"
        );
        setError(err.message);
        return reject(err);
      }
      return await w3iClient.unregister({ account }).then((res) => {
        setData(true);
        resolve(res);
      }).catch((e) => {
        setData(false);
        setError(e?.message ?? "Failed to unregister");
        reject(e);
      }).finally(() => {
        setIsLoading(false);
      });
    });
  };
  if (isLoading) {
    return {
      data: null,
      isLoading,
      error: null,
      unregister
    };
  }
  return {
    data,
    error,
    isLoading: false,
    unregister
  };
};

// src/hooks/useUnsubscribe.ts
import { useState as useState11 } from "react";
var useUnsubscribe = (account, domain) => {
  const { data: w3iClient } = useWeb3InboxClient();
  const [data, setData] = useState11(false);
  const [error, setError] = useState11(null);
  const [isLoading, setIsLoading] = useState11(false);
  const unsubscribe = async () => {
    setError(null);
    setIsLoading(true);
    return new Promise(async (resolve, reject) => {
      if (!w3iClient) {
        const err = new Error("Web3InboxClient is not ready, cannot subscribe");
        setError(err.message);
        return reject(err);
      }
      return await w3iClient.unsubscribeFromDapp(account, domain).then((res) => {
        setData(true);
        resolve(res);
      }).catch((e) => {
        setData(false);
        setError(e?.message ?? "Failed to unsubscribe");
        reject(e);
      }).finally(() => {
        setIsLoading(false);
      });
    });
  };
  if (isLoading) {
    return {
      data: null,
      error: null,
      isLoading,
      unsubscribe
    };
  }
  return {
    data,
    error,
    isLoading,
    unsubscribe
  };
};

// src/hooks/useWeb3InboxAccount.ts
import { useEffect as useEffect6, useState as useState12 } from "react";
var useWeb3InboxAccount = (initialAccount) => {
  const { data: w3iClient } = useWeb3InboxClient();
  const { account, registration } = useClientState();
  const [isRegistered, setIsRegistered] = useState12(false);
  const [data, setData] = useState12(account);
  const [error, setError] = useState12(null);
  const [isLoading, setIsLoading] = useState12(false);
  const identityKey = isRegistered && registration ? registration.identity : null;
  const setAccount = async (account2) => {
    setError(null);
    setIsLoading(true);
    if (!w3iClient) {
      throw new Error("Web3InboxClient is not ready, cannot set account");
    }
    return w3iClient.setAccount(account2).catch((e) => {
      setError(e?.message ?? "Failed to set account");
    }).finally(() => {
      setIsLoading(false);
    });
  };
  useEffect6(() => {
    const registrationStatus = registration ? registration.account === account : false;
    setIsRegistered(registrationStatus);
  }, [account, registration]);
  useEffect6(() => {
    if (!initialAccount || !w3iClient)
      return;
    setAccount(initialAccount);
  }, [w3iClient, initialAccount]);
  useEffect6(() => {
    setData(account);
  }, [account]);
  if (isLoading) {
    return {
      data: null,
      identityKey,
      isRegistered,
      error,
      isLoading,
      setAccount
    };
  }
  return {
    data,
    identityKey,
    isRegistered,
    error,
    isLoading,
    setAccount
  };
};

// src/utils/initWeb3InboxClient.ts
import { Web3InboxClient as Web3InboxClient3 } from "@web3inbox/core";

// package.json
var name = "@web3inbox/react";
var version = "1.4.0";

// src/utils/initWeb3InboxClient.ts
var initWeb3InboxClient = ({
  projectId,
  domain,
  allApps,
  logLevel,
  rpcUrlBuilder
}) => {
  return Web3InboxClient3.init({
    projectId,
    domain,
    allApps,
    logLevel,
    rpcUrlBuilder,
    sdkVersionMapEntries: {
      [name]: version
    }
  });
};
export {
  initWeb3InboxClient,
  useAllSubscriptions,
  useNotificationTypes,
  useNotifications,
  usePrepareRegistration,
  usePrepareRegistrationWithRecaps,
  useRegister,
  useSubscribe,
  useSubscription,
  useUnregister,
  useUnsubscribe,
  useWeb3InboxAccount,
  useWeb3InboxClient
};
